import * as React from "react";

import HelpIcon from "@mui/icons-material/Help";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import Stack from "@mui/material/Stack";

import { useLogMountTime } from "../../log";
import CloseableDialogTitle from "../common/CloseableDialogTitle";
import { usePairingContext } from "../hooks/pairingStatus";
import { useStatus } from "../hooks/status";
import useIntercom from "../hooks/useIntercom";
import useInterval from "../hooks/useInterval";

import useDialog from "./useDialog";

const MAX_RECONNECT_SECONDS = 5;
const SHOW_UNPAIR_MILLISECONDS = 3_000;

export default function ServiceUnavailableDialog(): React.JSX.Element {
    useLogMountTime("ServiceUnavailableDialog");

    const { networkStateCode, status } = useStatus();
    const { setDialogType } = useDialog();
    const intercom = useIntercom();
    const { unpair } = usePairingContext();
    const [nextAttemptSeconds, setNextAttemptSeconds] = React.useState(
        MAX_RECONNECT_SECONDS,
    );
    const [firstRender] = React.useState(performance.now());

    const onClose = React.useCallback(() => {
        setDialogType(null);
    }, [setDialogType]);

    const onClickUnpair = React.useCallback(
        (e: React.MouseEvent<HTMLButtonElement>) => {
            e.preventDefault();
            unpair();
            setDialogType("ShutdownHardwareChecklist");
        },
        [setDialogType, unpair],
    );

    const onClickSupport = React.useCallback(() => {
        const trainer = status?.clientId ? ` (trainer ${status.clientId})` : "";
        intercom.newMessage(
            `My trainer lost its network connection${trainer}. Status: ${networkStateCode}`,
        );
    }, [intercom, networkStateCode, status?.clientId]);

    useInterval(() => {
        setNextAttemptSeconds(
            nextAttemptSeconds === 1
                ? MAX_RECONNECT_SECONDS
                : nextAttemptSeconds - 1,
        );
    }, 1000);

    // Auto close this dialog if the network reconnects
    React.useEffect(() => {
        if (networkStateCode === "Connected") {
            onClose();
        }
    }, [networkStateCode, onClose]);

    const secondsDisplay = `${nextAttemptSeconds} second${nextAttemptSeconds > 1 ? "s" : ""}`;
    const showDisconnect =
        firstRender + SHOW_UNPAIR_MILLISECONDS < performance.now();

    return (
        <>
            <CloseableDialogTitle variant="h4">
                Network Connection Problem
            </CloseableDialogTitle>
            <DialogContent dividers>
                <DialogContentText>
                    <CircularProgress color="primary" />
                </DialogContentText>
                <DialogContentText>
                    {networkStateCode === "ServiceConnectionDown"
                        ? "We're sorry. Connection with the Volley service couldn't be established."
                        : `The trainer will attempt to reconnect in ${secondsDisplay}.`}
                </DialogContentText>
                <DialogContentText>
                    If a connection is not re-established, please contact
                    support.
                </DialogContentText>
            </DialogContent>
            <DialogActions>
                <Stack width="100%" spacing={1}>
                    <Button
                        variant="contained"
                        color="info"
                        fullWidth
                        startIcon={<HelpIcon />}
                        onClick={onClickSupport}
                    >
                        Contact Support
                    </Button>
                    {showDisconnect && (
                        <Button onClick={onClickUnpair}>Disconnect</Button>
                    )}
                </Stack>
            </DialogActions>
        </>
    );
}
